const { isEmpty } = require('lodash');
const { addDays, isAfter, isBefore } = require('date-fns');

// レビュー依頼ルール。セラー内に複数作成でき、注文がいずれかの有効ルールにマッチすれば送信対象になる。
// (Amazonは1注文1回しか送信できないため、複数ルールがマッチしても実送信は1回に収束する)

// 送信タイミング(出荷期限=latestShipDate の何日後に送るか)。
// FBAは配送予定日を取得できないため出荷期限を基準日とする。実送信可否は getSolicitationActions が最終判定。
const timingOptions = [
  { label: '出荷の5日後', value: 5 },
  { label: '出荷の7日後', value: 7 },
  { label: '出荷の10日後', value: 10 },
  { label: '出荷の14日後', value: 14 },
  { label: '出荷の21日後', value: 21 },
];

// ASIN絞り込みモード。
const asinModes = [
  { label: 'すべての商品', value: 'all' },
  { label: '指定ASINのみ', value: 'include' },
  { label: '指定ASINを除外', value: 'exclude' },
];

// 低評価除外の閾値(この星数以下の店舗評価を付けた注文を除外)。
// GET_SELLER_FEEDBACK_DATA は星1〜3を返すため、選べるのは3以下まで。
const lowRatingThresholds = [
  { label: '星3以下', value: 3 },
  { label: '星2以下', value: 2 },
  { label: '星1', value: 1 },
];
const DEFAULT_LOW_RATING_THRESHOLD = 3;

const fields = () => {
  return {
    name: {
      type: 'string',
      label: 'ルール名',
      validations: {
        required: v => !isEmpty(v),
      },
    },
    enabled: {
      type: 'boolean',
      label: 'このルールを有効にする',
      initialValue: true,
    },
    timingDays: {
      type: 'select',
      label: '送信タイミング',
      options: timingOptions,
      initialValue: 7,
    },
    asinMode: {
      type: 'select',
      label: '対象商品',
      options: asinModes,
      initialValue: 'all',
    },
    asins: {
      type: 'string',
      label: 'ASIN(カンマ区切り)',
      initialValue: '',
      hidden: state => state.asinMode === 'all',
    },
    excludeRefunded: {
      type: 'boolean',
      label: '返金済みの注文を除外する',
      initialValue: true,
    },
    excludeLowRating: {
      type: 'boolean',
      label: '低評価を付けた注文を除外する',
      initialValue: true,
    },
    lowRatingThreshold: {
      type: 'select',
      label: '除外する評価',
      options: lowRatingThresholds,
      initialValue: DEFAULT_LOW_RATING_THRESHOLD,
      hidden: state => state.excludeLowRating !== true,
    },
  };
};

// "B0xxx, B0yyy" のような文字列を ASIN 配列に正規化する。
const parseAsins = (asins) => {
  if (Array.isArray(asins)) return asins;
  return String(asins || '')
    .split(/[,\s]+/)
    .map(_ => _.trim())
    .filter(Boolean);
};

// 注文がルールのASIN条件にマッチするか。order.asins は syncOrders で明細から保存する。
// 明細が未取得(繰り越し中)の注文は判定できないため、取得されるまで対象外にする(excludeで誤送信しないため)。
const matchesAsin = (order, rule) => {
  if (rule.asinMode !== 'include' && rule.asinMode !== 'exclude') return true;
  const ruleAsins = parseAsins(rule.asins);
  if (ruleAsins.length === 0) return rule.asinMode === 'exclude'; // 指定なし: includeは対象なし/excludeは全対象
  if (isEmpty(order.asins)) return false;
  const hit = order.asins.some(_ => ruleAsins.includes(_));
  return rule.asinMode === 'include' ? hit : !hit;
};

// 送信タイミングの基準日。配送予定日(MFNで入る)優先、無ければ出荷期限(FBA)。
const solicitationBaseDate = (order) => order.latestDeliveryDate || order.latestShipDate || null;

// 注文が「送信ウィンドウ内」か。下限=基準日+timingDays、上限=基準日+30(Amazonの送信可能期限)。
// 自前判定は無駄打ち削減の緩い事前フィルタ。最終可否は getSolicitationActions が握る。
const isWithinWindow = (order, timingDays, now) => {
  const baseDate = solicitationBaseDate(order);
  if (!baseDate) return false;
  const windowStart = addDays(new Date(baseDate), timingDays);
  const windowEnd = addDays(new Date(baseDate), 30);
  return !isBefore(now, windowStart) && !isAfter(now, windowEnd);
};

// 注文が低評価除外に該当するか。ratingByOrderId は 注文ID→その注文に付いた最低の店舗評価(星) の Map。
// ルールの閾値(lowRatingThreshold、既定3)以下の評価が付いていれば除外対象。
const isLowRatedByRule = (order, rule, ratingByOrderId) => {
  if (!rule.excludeLowRating) return false;
  const rating = ratingByOrderId?.get(order.amazonOrderId);
  if (rating == null) return false;
  const threshold = rule.lowRatingThreshold ?? DEFAULT_LOW_RATING_THRESHOLD;
  return rating <= threshold;
};

// 注文がこのルールで送信対象になるか。除外条件・ASIN条件・タイミング窓をすべて満たすか判定する。
// refundedOrderIds は Set、ratingByOrderId は 注文ID→最低評価 の Map。呼び出し側で一度だけ構築して渡す。
const matchesRule = (order, rule, { refundedOrderIds, ratingByOrderId, now = new Date() } = {}) => {
  if (rule.enabled === false) return false;
  if (order.orderStatus === 'Canceled') return false;
  if (rule.excludeRefunded && refundedOrderIds?.has(order.amazonOrderId)) return false;
  if (isLowRatedByRule(order, rule, ratingByOrderId)) return false;
  if (!matchesAsin(order, rule)) return false;
  if (!isWithinWindow(order, rule.timingDays, now)) return false;
  return true;
};

exports.fields = fields;
exports.timingOptions = timingOptions;
exports.asinModes = asinModes;
exports.lowRatingThresholds = lowRatingThresholds;
exports.parseAsins = parseAsins;
exports.solicitationBaseDate = solicitationBaseDate;
exports.isWithinWindow = isWithinWindow;
exports.isLowRatedByRule = isLowRatedByRule;
exports.matchesRule = matchesRule;
